"use client";

import Link from "next/link";
import clsx from "clsx";
import { PortableText, type PortableTextComponents } from "@portabletext/react";
import type { PortableTextBlock } from "@portabletext/types";

type Props = {
    value: PortableTextBlock[];
    paragraphClassName?: string;
    linkClassName?: string;
    className?: string;
};

export function PortableTextBody({
    value,
    paragraphClassName = "text-base",
    linkClassName = "underline underline-offset-4 hover:text-accent",
    className,
}: Props) {
    if (!value?.length) {
        return null;
    }

    const components: PortableTextComponents = {
        block: {
            normal: ({ children }) => <p className={paragraphClassName}>{children}</p>,
            h3: ({ children }) => (
                <h3 className={clsx("mt-6 mb-2 text-2xl font-medium md:text-3xl", paragraphClassName)}>
                    {children}
                </h3>
            ),
        },
        list: {
            bullet: ({ children }) => <ul className={paragraphClassName}>{children}</ul>,
            number: ({ children }) => <ol className={paragraphClassName}>{children}</ol>,
        },
        listItem: {
            bullet: ({ children }) => <li className={paragraphClassName}>{children}</li>,
            number: ({ children }) => <li className={paragraphClassName}>{children}</li>,
        },
        marks: {
            link: ({ children, value: linkValue }) => {
                const href = typeof linkValue?.href === "string" ? linkValue.href : "#";
                const isInternal = linkValue?.internal === true;

                if (isInternal && href.startsWith("/")) {
                    return (
                        <Link href={href} className={linkClassName}>
                            {children}
                        </Link>
                    );
                }

                return (
                    <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={linkClassName}
                    >
                        {children}
                    </a>
                );
            },
        },
    };

    return (
        <div className={className}>
            <PortableText value={value} components={components} />
        </div>
    );
}
